import { PencilIcon, PlusIcon, TrashIcon, UserIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { GenderApiValues } from "@utils/constants";
import { setGenderValue } from "@utils/helpers";
import React from "react";

import { Button, Dialog, Headline, Paragraph, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  CaregiverInput,
  CreateLearnerCaregiverMutationVariables,
  useCreateLearnerCaregiverMutation,
} from "@api/graphql/types-and-hooks";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { getGenderValue } from "@pages/ClientDetailsSubPages/IntakeForm/IntakeFormSubPages/helpers";
import { OnboardingShell } from "@pages/Onboarding/OnboardingShell";

// A client the user has added to the step but not yet persisted. Clients are
// staged locally (so they can be freely edited/removed — there's no learner
// delete API and create returns no id) and created on Finish.
interface StagedClient {
  learner: CreateLearnerCaregiverMutationVariables["learner"];
  caregivers: CaregiverInput[];
}

// Replace the item at `index`, or append when `index` is null. Kept at module
// scope so the list-update callbacks stay shallow (avoids deeply nested inline
// functions in the JSX handlers).
function replaceOrAppend<T>(list: T[], index: number | null, item: T): T[] {
  if (index === null) return [...list, item];
  return list.map((existing, i) => (i === index ? item : existing));
}

function removeAt<T>(list: T[], index: number): T[] {
  return list.filter((_, i) => i !== index);
}

// Lightweight caregiver capture for onboarding. The in-app AddUpdateCaregiverDialogue
// depends on ClientProvider (useClientData), which isn't mounted on the onboarding
// route, so we collect just the basics (name / email) here. The full caregiver
// record can be enriched later from the client's profile.
interface OnboardingCaregiverDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  caregiver: CaregiverInput | null;
  onSave: (caregiver: CaregiverInput) => void;
}

const OnboardingCaregiverDialog: React.FC<OnboardingCaregiverDialogProps> = ({ open, setOpen, caregiver, onSave }) => {
  const { formState, onSubmit, RegisteredFormInput } = useFormUtils<CaregiverInput>({
    // Show every blank required field's error at once (not native one-at-a-time).
    useNativeValidation: false,
    mutationFn: async (params) => {
      onSave(params);
      setOpen(false);
    },
    defaultValues: {
      firstName: caregiver?.firstName ?? "",
      lastName: caregiver?.lastName ?? "",
      email: caregiver?.email ?? "",
    },
  });

  return (
    <FormContainer onSubmit={onSubmit}>
      <Dialog
        title={caregiver ? "Edit caregiver" : "Add a caregiver"}
        open={open}
        setOpen={setOpen}
        primaryButton={{ text: caregiver ? "Update" : "Add", onClick: onSubmit }}
        secondaryButton={{ text: "Cancel", onClick: () => setOpen(false) }}
        size="lg"
        overflowBehavior="scroll"
      >
        <div className="flex flex-col gap-4 p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <RegisteredFormInput formKey="firstName" inputSize="full" formState={formState} label="First Name" />
            <RegisteredFormInput formKey="lastName" inputSize="full" formState={formState} label="Last Name" />
          </div>
          <RegisteredFormInput
            formKey="email"
            inputSize="full"
            formState={formState}
            required={false}
            label="Email Address"
          />
        </div>
      </Dialog>
    </FormContainer>
  );
};

// The expandable add/edit client card. Holds its own form + caregiver state so
// each time it's opened (it's remounted via key) it starts from `initial` (the
// client being edited) or blank (a new client). "Create client" / "Save changes"
// validates and hands the staged client up; "Cancel" discards the card.
interface AddClientCardProps {
  initial: StagedClient | null;
  onSave: (client: StagedClient) => void;
  onCancel: () => void;
}

const AddClientCard: React.FC<AddClientCardProps> = ({ initial, onSave, onCancel }) => {
  const [caregiverList, setCaregiverList] = React.useState<CaregiverInput[]>(initial?.caregivers ?? []);
  const [isCaregiverDialogOpen, setIsCaregiverDialogOpen] = React.useState(false);
  const [selectedCaregiver, setSelectedCaregiver] = React.useState<CaregiverInput | null>(null);
  const [editIndex, setEditIndex] = React.useState<number | null>(null);

  const { formState, onSubmit, RegisteredFormInput, RegisteredFormSelected, watch } =
    useFormUtils<CreateLearnerCaregiverMutationVariables>({
      // Show every blank required field's error at once (not native one-at-a-time).
      useNativeValidation: false,
      mutationFn: async (params) => {
        onSave({ learner: params.learner, caregivers: caregiverList });
      },
      defaultValues: {
        learner: initial?.learner ?? {
          firstName: "",
          lastName: "",
          birthDate: "",
          gender: undefined,
        },
      },
    });

  const genderWatch = watch("learner.gender");

  const openCaregiverDialog = (caregiver: CaregiverInput | null, index: number | null) => {
    setSelectedCaregiver(caregiver);
    setEditIndex(index);
    setIsCaregiverDialogOpen(true);
  };
  const saveCaregiver = (caregiver: CaregiverInput) =>
    setCaregiverList((prev) => replaceOrAppend(prev, editIndex, caregiver));
  const removeCaregiver = (index: number) => setCaregiverList((prev) => removeAt(prev, index));

  const genderItems: SelectItem[] = Object.values(GenderApiValues).map((o) => ({ primary: getGenderValue(o) ?? "" }));

  return (
    <div className="rounded-xl border border-border-medium p-5">
      <p className="text-base font-semibold text-text-primary">Client details</p>
      <Small className="mt-1 block text-text-tertiary">Tell us a little about the client you&apos;re adding.</Small>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <RegisteredFormInput formKey="learner.firstName" inputSize="full" formState={formState} label="First Name" />
        <RegisteredFormInput formKey="learner.lastName" inputSize="full" formState={formState} label="Last Name" />
        <RegisteredFormInput
          formKey="learner.birthDate"
          inputSize="full"
          type="date"
          formState={formState}
          label="Date of Birth"
        />
        <RegisteredFormSelected
          formKey="learner.gender"
          formState={formState}
          placeholderText=""
          title="Gender"
          required={false}
          items={genderItems}
        />
        {genderWatch === "Gender identity is not listed" && (
          <RegisteredFormInput
            formKey="learner.specifyGender"
            inputSize="full"
            formState={formState}
            required={false}
            label="Specify Gender"
          />
        )}
      </div>

      <div className="mt-6">
        <div className="flex items-baseline justify-between">
          <p className="text-sm font-semibold text-text-primary">Caregivers</p>
          <Small className="text-text-tertiary">Optional</Small>
        </div>

        <div className="mt-3 flex flex-col gap-3">
          {caregiverList.map((caregiver, index) => (
            <div
              key={`${caregiver.firstName}-${caregiver.lastName}-${index}`}
              className="flex items-center gap-4 rounded-lg border border-border-medium p-3"
            >
              <div className="flex flex-1 flex-col">
                <span className="text-sm font-medium text-text-primary">
                  {caregiver.firstName} {caregiver.lastName}
                </span>
                {caregiver.email && <span className="text-xs text-text-secondary">{caregiver.email}</span>}
              </div>
              <button
                type="button"
                aria-label="Edit caregiver"
                onClick={() => openCaregiverDialog(caregiver, index)}
                className="text-text-tertiary transition-colors hover:text-text-secondary"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Remove caregiver"
                onClick={() => removeCaregiver(index)}
                className="text-text-tertiary transition-colors hover:text-text-secondary"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          ))}

          {caregiverList.length === 0 && (
            <Button
              className="self-start"
              text="Add caregiver"
              variant="secondary"
              buttonStyle="border"
              onClick={() => openCaregiverDialog(null, null)}
            />
          )}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button text="Cancel" variant="secondary" buttonStyle="ghost" onClick={onCancel} />
        <Button
          text={initial ? "Save changes" : "Create client"}
          variant="primary"
          buttonStyle="border"
          onClick={onSubmit}
        />
      </div>

      {/* Mount only while open so each open is a fresh form instance — otherwise
          react-hook-form would retain the previous caregiver's submitted values. */}
      {isCaregiverDialogOpen && (
        <OnboardingCaregiverDialog
          open
          setOpen={setIsCaregiverDialogOpen}
          caregiver={selectedCaregiver}
          onSave={saveCaregiver}
        />
      )}
    </div>
  );
};

interface CreateFirstClientStepProps {
  onBack: () => void;
  // Onboarding done (clients created or skipped) → hand off to the app landing.
  onFinish: () => void;
  onSkip: () => void;
  currentStep: number;
  totalSteps: number;
}

const buildCreateParams = (client: StagedClient): CreateLearnerCaregiverMutationVariables => {
  const caregiver = client.caregivers.length ? client.caregivers[0] : null;
  return {
    learner: {
      ...client.learner,
      gender: client.learner.gender ? setGenderValue(client.learner.gender) : undefined,
    },
    caregiver: caregiver
      ? {
          ...caregiver,
          smsAgreement: Boolean(caregiver?.smsAgreement),
          isPrimary: true,
        }
      : undefined,
    sendIntake: false,
    providerIds: null,
  };
};

// Step 2 (no-EHR flow) — "Create your first client". Orgs that have no EHR
// scraper and no direct integration never connect/import, so the Connect-EHR and
// Import-clients steps don't apply to them. Instead they can manually add clients
// here using the same "client-lite" inputs as the in-app Add Client form
// (first/last name, DOB, gender, caregivers), reusing useCreateLearnerCaregiver.
//
// This is the last step, so the footer primary CTA is "Finish" (matching every
// other step) — NOT the client-create action. "Add client" expands a card to
// stage a client; staged clients list with edit/remove actions and are persisted
// on Finish. Creating is optional — Finish / Skip both move on to the same end
// state. (Staging rather than persisting per-card is deliberate: there's no
// learner-delete API and create returns no id, so a persisted client couldn't be
// removed or edited.)
export const CreateFirstClientStep: React.FC<CreateFirstClientStepProps> = ({
  onBack,
  onFinish,
  onSkip,
  currentStep,
  totalSteps,
}) => {
  const [clients, setClients] = React.useState<StagedClient[]>([]);
  const [isCardOpen, setIsCardOpen] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [isFinishing, setIsFinishing] = React.useState(false);

  // Invalidate every learners query (keys vary by auth/roles across screens) so
  // created clients show up when the user reaches /clients — a single keyed
  // refetch here wouldn't match those other cache entries.
  const queryClient = useQueryClient();
  const createLearnerCaregiver = useCreateLearnerCaregiverMutation({});

  const openNewCard = () => {
    setEditingIndex(null);
    setIsCardOpen(true);
  };

  const editClient = (index: number) => {
    setEditingIndex(index);
    setIsCardOpen(true);
  };

  const removeClient = (index: number) => {
    setClients((prev) => removeAt(prev, index));
    // Removing shifts list indices. If an edit card is open, its editingIndex is
    // now stale and a save would overwrite the wrong row (or drop another client),
    // so cancel the edit. (A new-client card — editingIndex null — is unaffected.)
    if (editingIndex !== null) {
      setIsCardOpen(false);
      setEditingIndex(null);
    }
  };

  const handleSaveClient = (client: StagedClient) => {
    setClients((prev) => replaceOrAppend(prev, editingIndex, client));
    setIsCardOpen(false);
    setEditingIndex(null);
  };

  // Persist all staged clients, then advance. Successfully-created clients are
  // dropped from the list so a retry after a partial failure can't duplicate them.
  const handleFinish = async () => {
    if (clients.length === 0) {
      onFinish();
      return;
    }
    setIsFinishing(true);
    const failed: StagedClient[] = [];
    let createdCount = 0;
    for (const client of clients) {
      try {
        const res = await createLearnerCaregiver.mutateAsync(buildCreateParams(client));
        if (res.createLearnerCaregiver?.status) createdCount += 1;
        else failed.push(client);
      } catch {
        failed.push(client);
      }
    }
    if (createdCount > 0) queryClient.invalidateQueries({ queryKey: ["learners"] });
    if (failed.length) {
      setClients(failed);
      setIsFinishing(false);
      notifyError(
        `Couldn't create ${failed.length} client${failed.length > 1 ? "s" : ""}. Please review and try again.`,
      );
    } else {
      notifySuccess(`${createdCount} client${createdCount > 1 ? "s" : ""} created`);
      onFinish();
    }
  };

  const editing = editingIndex === null ? null : clients[editingIndex];

  return (
    <OnboardingShell
      onContinue={handleFinish}
      onBack={onBack}
      onSkip={onSkip}
      continueLabel="Finish"
      continueDisabled={isFinishing}
      // Lock the escapes while clients are being created so an in-flight create
      // can't complete after the user has navigated away (creating clients they
      // believed they skipped).
      backDisabled={isFinishing}
      skipDisabled={isFinishing}
      currentStep={currentStep}
      totalSteps={totalSteps}
    >
      <Headline size="medium">Create your first client</Headline>
      <Paragraph size="small" className="mt-2 text-text-secondary">
        Add a client to get started, or skip for now and add clients later. You can capture their full details,
        documents, and caregivers anytime from their profile.
      </Paragraph>

      <div className="mt-8 flex flex-col gap-3">
        {clients.map((client, index) => (
          <div
            key={`${client.learner.firstName}-${client.learner.lastName}-${index}`}
            className="flex items-center gap-3 rounded-lg border border-border-subtle bg-limestone-50 p-4"
          >
            <UserIcon className="h-5 w-5 text-text-tertiary" />
            <span className="flex-1 text-base font-medium text-text-primary">
              {client.learner.firstName} {client.learner.lastName}
            </span>
            <button
              type="button"
              aria-label="Edit client"
              onClick={() => editClient(index)}
              className="text-text-tertiary transition-colors hover:text-text-secondary"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Remove client"
              onClick={() => removeClient(index)}
              className="text-text-tertiary transition-colors hover:text-text-secondary"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        ))}

        {isCardOpen ? (
          <AddClientCard
            key={editingIndex === null ? `new-${clients.length}` : `edit-${editingIndex}`}
            initial={editing}
            onSave={handleSaveClient}
            onCancel={() => {
              setIsCardOpen(false);
              setEditingIndex(null);
            }}
          />
        ) : (
          <Button
            className="self-start"
            text={
              <span className="flex items-center gap-1.5">
                <PlusIcon className="h-4 w-4" />
                {clients.length ? "Add another client" : "Add client"}
              </span>
            }
            variant="primary"
            buttonStyle="border"
            onClick={openNewCard}
          />
        )}
      </div>
    </OnboardingShell>
  );
};
